import React from 'react';
import {
  TypedUseSelectorHook,
  useSelector as useReduxSelector,
} from 'react-redux';

import moment from 'moment-timezone';

import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { RootState } from '../../../app/rootReducer';
import { isAdmin } from '../../../common/actionHelper';
import { DecisionIcon } from '../../../common/components/DecisionIcon';
import { Kebab } from '../../../common/components/KebabMenu';
import {
  CarrierDecisionType,
  DecisionType,
  transformRateClass,
} from '../../../common/constants/decision';
import { ApplicationLifecycle } from '../../../common/constants/lifecycle';
import { Products } from '../../../common/constants/products';
import { Resource } from '../../../common/constants/resource';
import { NoraPermissionV2 } from '../../../common/constants/roles';
import { ordinalSuffix } from '../../../common/helpers';
import { useAccess } from '../../../common/hooks/useAccess';
import { IKeyValMap } from '../../../common/interfaces/IKeyValMap';
import { ProductFactory } from '../../../common/productConfig/productFactory';
import { ITask } from '../../tasks/interfaces/ITask';
import { IDecision } from '../interfaces/IDecision';
import { IRiderInfo } from '../interfaces/IRiderInfo';
import styles from '../styles.module.scss';

export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

/**
 * From our PRD the logic is:
 * if rating of null || 0 || 100% then no table rating
 * if over 100 e.g. 125% then +25%, if 150% then +50% and so on.
 */
const getTableRating = (tableRating: string | undefined) => {
  const rating: number | null = tableRating ? parseInt(tableRating, 10) : null;
  if (rating === null || rating === 0 || rating === 100) {
    return Resource.NORA_M_DASH;
  } else if (rating > 100) {
    return `${rating}%`;
  } else {
    // Shouldn't happen as we're not supposed to be < 100 but ¯\_(ツ)_/¯
    return Resource.NORA_M_DASH;
  }
};

const getDebitsFromTableRating = (tableRating: string | undefined): string => {
  const rating: number | null = tableRating ? parseInt(tableRating, 10) : null;
  if (rating === null || rating === 0) {
    return Resource.NORA_M_DASH;
  } else if (rating >= 100) {
    return (rating - 100).toString();
  } else {
    // Shouldn't happen as we're not supposed to be < 100 but ¯\_(ツ)_/¯
    return Resource.NORA_M_DASH;
  }
};

/**
 * Approved application - OTAF Reason and Amendment in the drop down.
 * Declined application - Decline Reason and Amendment in the drop down
 * Withdrawn application - won't actually have the drop down - it wouldn't
 * have a reason or an amendment
 *
 * Modal content:
 * Decline and OTAF Reasons have title, reasons bullets, and description
 * Amendment will just have the description
 */
const kabobablesMap: IKeyValMap = {
  final: true,
  random_holdout: true,
  post_issue_audit: true,
  applied_for: false,
  engine: false,
  initial_audit: true,
  final_audit: true,
  legal_check: true,
  reinstatement: true,
  preliminary_final: true,
  preliminary_pia: true,
  planf: true,
};

const getMoreColumn = (
  decision: IDecision,
  onEditDecision?: (decision: IDecision) => void,
  lifecycle?: string,
  allDecisions?: IDecision[],
  isUserAdmin?: boolean,
) => {
  const kabobMenuItems: IKeyValMap[] = [];
  // Add null check to prevent crash if decisionType is null/undefined
  if (!decision.decisionType) {
    return kabobMenuItems;
  }
  const decisionTypeKey = decision.decisionType
    .toLowerCase()
    .split(' ')
    .join('_');

  const hasRiders =
    decision.ineligibleRiders?.length || decision.eligibleRiders?.length;
  const isKabobableDecision = kabobablesMap[decisionTypeKey] || hasRiders;

  /**
   * Once we have multiple decision rows besides "applied for" e.g. "Final"
   * this will be the logic to dynamically populate the Kabob and Modal
   */
  const getKabobItems = () => {
    if (isKabobableDecision) {
      if (decision.otafReasons) {
        kabobMenuItems.push({
          name: Resource.OTAF_TITLE,
          reasons: decision.otafReasons,
          description: decision.otafDescription,
        });
      }

      if (decision.carrierUnderwriterName) {
        kabobMenuItems.push({
          name: `Decision by: ${decision.carrierUnderwriterName}`,
        });
      }

      if (decision.doctorInfo) {
        kabobMenuItems.push({
          name: Resource.DOC_INFO_TITLE,
          reasons: [],
          description:
            decision.doctorInfo.toUpperCase() === Resource.NORA_NOT_APPLICABLE
              ? Resource.DOCTOR_INFO_NOT_PROVIDED
              : decision.doctorInfo,
        });
      }

      if (decision.amendment) {
        kabobMenuItems.push({
          name: Resource.AMENDMENT_TITLE,
          reasons: [],
          description: decision.amendment,
        });
      }

      if (decision.reviewStatus) {
        kabobMenuItems.push({
          name: Resource.REVIEW_STATUS_TITLE,
          reasons: [],
          description: decision.reviewStatus,
        });
      }

      if (decision.mibCodes) {
        kabobMenuItems.push({
          name: Resource.MIB_CODES_TITLE,
          reasons: [],
          description: decision.mibCodes.join(', '),
        });
      }

      if (decision.declineReasons) {
        kabobMenuItems.push({
          name: CarrierDecisionType.includes(decision.decisionType)
            ? Resource.DISAGREEMENT_REASON
            : Resource.DECLINE_TITLE,
          reasons: decision.declineReasons,
          description: decision.declineDescription,
          conditions: decision.reason?.declineConditions,
        });
      }

      if (decision.reason?.postponeReasons) {
        kabobMenuItems.push({
          name: Resource.POSTPONE_TITLE,
          reasons: decision.reason?.postponeReasons,
          description: decision.reason?.postponeDescription,
          conditions: decision.reason?.postponeConditions,
        });
      }

      if (decision.deleteReason) {
        kabobMenuItems.push({
          name: Resource.UPDATE_TITLE,
          reasons: [],
          description: decision.deleteReason,
        });
      }

      if (decision.reason?.ineligibleReasons) {
        kabobMenuItems.push({
          name: Resource.INELIGIBLE_TITLE,
          reasons: decision.reason?.ineligibleReasons,
          description: decision.reason?.ineligibleDescription,
        });
      }
      if (decision.eligibleRiders?.length) {
        const eligibleRidersWithReason = decision.eligibleRiders.map(r =>
          mapRiderToReason(r, decision.ridersInfo),
        );
        kabobMenuItems.push({
          name: Resource.ELIGIBLE_RIDERS,
          riders: eligibleRidersWithReason,
        });
      }
      if (decision.ineligibleRiders?.length) {
        const ineligibleRidersWithReason = decision.ineligibleRiders.map(r =>
          mapRiderToReason(r, decision.ridersInfo),
        );

        kabobMenuItems.push({
          name: Resource.INELIGIBLE_RIDERS,
          riders: ineligibleRidersWithReason,
        });
      }
    }

    // Add Edit option for the last PIA or PLAN_F decision when lifecycle is CLR and user is admin
    if (
      onEditDecision &&
      isUserAdmin &&
      lifecycle === ApplicationLifecycle.CLR &&
      allDecisions &&
      (decision.decisionType === DecisionType.PIA ||
        decision.decisionType === DecisionType.PLAN_F)
    ) {
      const piaAndPlanFDecisions = allDecisions.filter(
        d =>
          !d.ignored &&
          (d.decisionType === DecisionType.PIA ||
            d.decisionType === DecisionType.PLAN_F),
      );

      const sortedDecisions = [...piaAndPlanFDecisions].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );

      // Only show Edit if this is the most recent PIA or PLAN_F decision
      if (sortedDecisions.length > 0 && sortedDecisions[0].id === decision.id) {
        kabobMenuItems.unshift({
          name: 'Edit',
          onClick: () => onEditDecision(decision),
        });
      }
    }

    return kabobMenuItems;
  };
  return isKabobableDecision ? getKabobItems() : null;
};

const mapRiderToReason = (
  riderName: string,
  ridersInfo: IRiderInfo[] | undefined,
): string => {
  const riderInfo = ridersInfo?.find(info => info.rider === riderName);
  const riderWithReason = riderInfo?.reason
    ? riderName + ': ' + riderInfo.reason
    : riderName;
  return riderWithReason;
};

// TODO: this should either be done on server or it should return a `displayText` or something
const transformTobaccoClass = (tobaccoClass: string) => {
  return tobaccoClass === 'NonTobacco' ? 'Non-Tobacco' : tobaccoClass;
};

const getDebitsDisplay = (debits: number, debitsFromPA: string) => {
  const debitsFromPAInt =
    debitsFromPA !== Resource.NORA_M_DASH ? parseInt(debitsFromPA, 10) : 0;
  const debitsDisplay = Number.isFinite(debits) ? debits : debitsFromPAInt;
  if (debitsFromPAInt !== debits) {
    return `${debitsDisplay} (${debitsFromPA})`;
  }
  return debitsDisplay;
};

const formatData = (
  decisions: IDecision[],
  onEditDecision?: (decision: IDecision) => void,
  lifecycle?: string,
  isUserAdmin?: boolean,
) => {
  // Compute ordinals for edited decisions. For Final, PIA, PlanF, Preliminary Final, Preliminary PIA decisions separately.
  const ordinalReducer = (acc: IKeyValMap, decision: IDecision, i: number) =>
    Object.assign(acc, { [decision.id]: ` (${ordinalSuffix(i + 1)})` });
  let updateIndices: IKeyValMap = decisions
    .filter(d => d.deleteReason && d.decisionType === DecisionType.FINAL)
    .reduce(ordinalReducer, {});
  updateIndices = decisions
    .filter(d => d.deleteReason && d.decisionType === DecisionType.PIA)
    .reduce(ordinalReducer, updateIndices);
  updateIndices = decisions
    .filter(d => d.deleteReason && d.decisionType === DecisionType.PLAN_F)
    .reduce(ordinalReducer, updateIndices);
  updateIndices = decisions
    .filter(
      d => d.deleteReason && d.decisionType === DecisionType.PRELIMINARY_FINAL,
    )
    .reduce(ordinalReducer, updateIndices);
  updateIndices = decisions
    .filter(
      d => d.deleteReason && d.decisionType === DecisionType.PRELIMINARY_PIA,
    )
    .reduce(ordinalReducer, updateIndices);

  const formattedTableData = decisions.map(decision => {
    const debitsFromPA = getDebitsFromTableRating(decision.tableRating);
    const debits = decision.debits ? decision.debits : 0;

    return {
      decisionType:
        decision.decisionType +
        (decision.isAutoDecision && decision?.ignored
          ? ' (Auto, ignored)'
          : decision.isAutoDecision
            ? ' (Auto)'
            : '') +
        (updateIndices[decision.id] || ''),
      rateClass: transformRateClass(decision.rateClass),
      decision: (
        <DecisionIcon
          decision={decision.decision}
          decisionType={decision.decisionType}
        />
      ),
      coverage: decision.coverage
        ? `$${decision.coverage.toLocaleString()}`
        : Resource.NORA_M_DASH,
      term: decision.term ? `${decision.term} Years` : Resource.NORA_M_DASH,
      tobaccoClass: transformTobaccoClass(decision.tobaccoClass),
      tableRating: getTableRating(decision.tableRating),

      debits:
        decision.decisionType !== DecisionType.AF
          ? getDebitsDisplay(debits, debitsFromPA)
          : Number.isFinite(debits)
            ? debits
            : getDebitsFromTableRating(decision.tableRating),

      // This will map to `cellValue` in GridView.tsx and be used as the `items`
      // passed into the NoraKebob menu component there.
      more: getMoreColumn(
        decision,
        onEditDecision,
        lifecycle,
        decisions,
        isUserAdmin,
      ),
      createdAt: moment(decision.createdAt).format('MMM DD, YYYY'),
    };
  });
  return formattedTableData;
};

export const Decision = ({
  title,
  decisions,
  product,
  carrier,
  subCategory,
  decisionTask,
  onEditDecision,
  applicationLifecycle,
}: {
  title: string;
  decisions: IDecision[];
  product: string;
  carrier: string;
  subCategory: string;
  decisionTask: ITask | undefined;
  onEditDecision?: (decision: IDecision) => void;
  applicationLifecycle?: string;
}) => {
  const canEditClrDecision = useAccess({
    permission: NoraPermissionV2.EDIT_DECISION_LEADING_TO_CLR,
    legacyCheck: isAdmin,
  });
  const productConfig = ProductFactory.getProductConfig(
    carrier,
    product,
    subCategory,
  );
  const shouldShowTerm = ![
    Products.IUL as string,
    Products.NAIULACUM as string,
  ].includes(product);
  const formattedTableData = formatData(
    decisions,
    onEditDecision,
    applicationLifecycle,
    canEditClrDecision,
  );

  if (!decisionTask) {
    return null;
  }

  return (
    <div data-label={'decision_section'} data-section-id={'Decision table'}>
      <Box id={decisionTask.id.toString()} className={styles.title}>
        <Typography variant="h6" component="span">
          {title}
        </Typography>
      </Box>

      <Box mt={2}>
        <TableContainer component={Paper} variant="outlined" square>
          <Table aria-label="Decisions table">
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography variant="overline" color="textSecondary">
                    type
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="overline" color="textSecondary">
                    date
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="overline" color="textSecondary">
                    decision
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="overline" color="textSecondary">
                    coverage
                  </Typography>
                </TableCell>
                {shouldShowTerm && (
                  <TableCell>
                    <Typography variant="overline" color="textSecondary">
                      term
                    </Typography>
                  </TableCell>
                )}
                <TableCell>
                  <Typography variant="overline" color="textSecondary">
                    class
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="overline" color="textSecondary">
                    tobacco
                  </Typography>
                </TableCell>
                {productConfig?.hasDebits() && (
                  <TableCell>
                    <Typography variant="overline" color="textSecondary">
                      debits
                    </Typography>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {formattedTableData.map((row, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Box
                      display="flex"
                      flexDirection="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box mr={1}>
                        <Typography variant="body2" title={row.decisionType}>
                          {row.decisionType}
                        </Typography>
                      </Box>
                      <Kebab options={row.more} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      noWrap={true}
                      style={{ fontVariantNumeric: 'ordinal tabular-nums' }}
                    >
                      {row.createdAt}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.decision}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      style={{ fontVariantNumeric: 'ordinal tabular-nums' }}
                    >
                      {row.coverage}
                    </Typography>
                  </TableCell>
                  {shouldShowTerm && <TableCell>{row.term}</TableCell>}
                  <TableCell>{row.rateClass}</TableCell>
                  <TableCell>{row.tobaccoClass}</TableCell>
                  {productConfig?.hasDebits() && (
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontVariantNumeric: 'ordinal tabular-nums' }}
                      >
                        {row.debits}
                      </Typography>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </div>
  );
};
